import { FC, memo, useEffect } from 'react'
import { Waypoint } from 'react-waypoint'
import { Hidden, useMediaQuery } from '@material-ui/core'
import { useTheme } from '@material-ui/core/styles'
import gql from 'graphql-tag'
import styled from 'styled-components'
import {
  CoverLetterItemScreen_SideBar_CurrentUserFragment,
  CoverLetterStatus,
  useCoverLetterItemScreen_SideBarQuery,
} from 'generated/graphql'
import { getCoverLetterTypeFromTab } from '@app.feature/coverletter/modules/getCoverLetterTypeFromTab'
import { mobileBreakPoint } from 'app.styled/media'
import Footer from 'components/Footer'
import Pagination from 'components/Pagination'
import { useSearchStateContext } from 'screens/CoverLetterScreen/useSearchState'
import SideBarContent from './SideBarContent'

interface SideBarProps {
  onScrollEnter?: () => void
  onScrollLeave?: () => void
  currentUser?: CoverLetterItemScreen_SideBar_CurrentUserFragment
}

const SideBar: FC<SideBarProps> = ({
  onScrollEnter,
  onScrollLeave,
  currentUser,
}) => {
  const theme = useTheme()
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'))

  const context = useSearchStateContext()
  const { tabState: currentTab, search, orderBy, pagination, page } = context

  const pageSize = 20

  const { data, loading } = useCoverLetterItemScreen_SideBarQuery({
    variables: {
      filterBy: {
        ...search,
        types: getCoverLetterTypeFromTab(currentTab),
        status: CoverLetterStatus.PUBLISHED,
        numberOfFragments: 1,
        fragmentSize: 100,
      },
      orderBy: {
        field: orderBy?.orderField,
        direction: orderBy?.orderDirection,
      },
      pagination: { page, pageSize },
    },
  })

  const totalCount = data?.coverLetters?.totalCount ?? 0

  useEffect(() => {
    if (!isMdUp) {
      return
    }
    pagination.setNumberOfPages(Math.ceil(totalCount / pageSize))
    window.scrollTo(0, 0)
  }, [pageSize, totalCount])

  return (
    <StyledWrapper onWheel={(event) => event.stopPropagation()}>
      <Waypoint onEnter={onScrollEnter} onLeave={onScrollLeave} />
      <Hidden smDown implementation="css">
        <div className="total-count-wrapper">
          <p className="total-count">검색결과 {totalCount}건</p>
        </div>
      </Hidden>

      <div className="list">
        <SideBarContent
          coverLetterEdges={data?.coverLetters?.edges || []}
          currentUser={currentUser}
          loading={loading}
        />
        <div className="list-pagination">
          <Hidden smDown implementation="css">
            <Pagination pagination={pagination} />
            <Footer variant="mobile" className="footer" />
          </Hidden>
        </div>
      </div>
    </StyledWrapper>
  )
}

gql`
  fragment CoverLetterItemScreen_SideBar_currentUser on User {
    ...CoverLetterItemScreen_SideBarContent_currentUser
  }

  query CoverLetterItemScreen_SideBar(
    $filterBy: CoverLetterFilter!
    $orderBy: CoverLetterOrder
    $pagination: Pagination
  ) {
    coverLetters(
      filterBy: $filterBy
      orderBy: $orderBy
      pagination: $pagination
    ) {
      edges {
        node {
          id
        }
        ...CoverLetterItemScreen_SideBarContent_coverLetterElasticEdge
      }
      totalCount
    }
  }
`

export default memo(SideBar, (prevProps, nextProps) => {
  return (
    prevProps?.onScrollEnter === nextProps?.onScrollEnter &&
    prevProps?.onScrollLeave === nextProps?.onScrollLeave &&
    prevProps?.currentUser === nextProps?.currentUser
  )
})

const StyledWrapper = styled.div`
  width: 440px;
  border-right: 1px solid #eeeeee;

  @media (min-width: 960px) {
    flex: 1 1 0px;
    overflow-y: auto;
  }

  ${mobileBreakPoint} {
    width: 100%;
  }

  .list {
    ${mobileBreakPoint} {
      margin-top: 30px;
    }
  }

  .item {
    width: 100%;
    display: flex;
    padding: 20px;
    border-bottom: 1px solid #eee;
    align-items: center;

    ${mobileBreakPoint} {
      background-color: #fff;
    }
  }

  .total-count {
    font-size: 16px;
    font-weight: 500;
  }

  .total-count-wrapper {
    padding: 13px 20px;
    border-right: 1px solid #eee;
    height: 50px;
    display: flex;
    align-items: center;
    background-color: #fafafa;
  }

  .list-pagination {
    ${mobileBreakPoint} {
      padding-bottom: 20px;
    }
  }

  .footer {
    text-align: center;
    margin-top: 20px;
  }
`
